import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ClipboardList, Loader2, MessageSquareText, Presentation, RefreshCcw, Sparkles, Target,
  UserRound,
} from 'lucide-react';
import { apiService } from '../../services/api';
import { getProjectPriorityInfo } from '../../utils/projectPriority';
import { formatDuration, formatMeetingDate } from './meetingStatus';

const POLL_MS = 30000;

// Relecture de Nexus après la réunion (transcription de qualité) : faite, en cours, ou rien (pas de direct).
export const NexusReviewBadge = ({ state, compact = false }) => {
  if (state === 'done') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
        <Sparkles className="h-3 w-3" /> {compact ? 'Relue' : 'Relue par Nexus'}
      </span>
    );
  }
  if (state === 'pending') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700"
        title="Nexus relit la réunion à partir d'une transcription de meilleure qualité : les quoi de neuf et les synthèses peuvent encore s'affiner.">
        <Loader2 className="h-3 w-3 animate-spin" /> {compact ? 'Relecture…' : 'Nexus relit la réunion…'}
      </span>
    );
  }
  return null;
};

const Bullets = ({ items }) => (
  <ul className="space-y-1">
    {items.map((item) => (
      <li key={item} className="flex gap-2 text-sm leading-snug text-slate-700">
        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Section = ({ icon: Icon, title, count, children, hint }) => (
  <section className="space-y-2">
    <div className="flex items-baseline justify-between gap-3 px-1">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Icon className="h-4 w-4 text-indigo-500" /> {title}
        {count !== undefined && <span className="text-xs font-normal text-slate-400">{count}</span>}
      </h2>
      {hint && <p className="hidden text-xs text-slate-400 sm:block">{hint}</p>}
    </div>
    {children}
  </section>
);

const ProjectCard = ({ project }) => {
  const priority = getProjectPriorityInfo(project.priority);
  return (
    <article className="flex flex-col rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-start justify-between gap-2">
        <Link to={`/project/${project.project_id}`} className="min-w-0 truncate text-sm font-semibold text-slate-900 hover:text-indigo-700">
          {project.project_name}
        </Link>
        {priority && (
          <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${priority.className}`}
            title={`Priorité : P${priority.value} - ${priority.label}`}>
            P{priority.value}
          </span>
        )}
      </div>
      {project.news.length > 0 ? <Bullets items={project.news} /> : (
        <p className="text-sm italic text-slate-400">Pas de quoi de neuf noté.</p>
      )}
      {project.notes && <p className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">{project.notes}</p>}
      {project.news.length > 0 && (
        <p className="mt-auto pt-2 text-[11px] text-slate-400">
          {project.news_source === 'secretaire' ? 'Validé par le secrétaire' : 'Écrit par Nexus'}
        </p>
      )}
    </article>
  );
};

const PointCard = ({ point }) => {
  const done = ['treated', 'closed'].includes(String(point.status || '').toLowerCase());
  return (
    <article className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="min-w-0 text-sm font-semibold text-slate-900">{point.title}</h3>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${done ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
          {done ? 'Traité' : String(point.status || '').toLowerCase() === 'converted' ? 'Transformé en tension' : 'Ouvert'}
        </span>
      </div>
      {point.author_name && <p className="mb-2 text-[11px] text-slate-400">Proposé par {point.author_name}</p>}
      {point.summary.length > 0 && <Bullets items={point.summary} />}
      {point.notes && <p className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">{point.notes}</p>}
      {point.actions.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2">
          {point.actions.map((action) => (
            <li key={`${action.type}-${action.task_id || action.name}`} className="flex flex-wrap items-center gap-x-2 text-xs text-slate-600">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              <span className="font-medium">{action.type === 'objective' ? 'Objectif' : 'Tâche'} : {action.name}</span>
              {action.owner_name && <span className="text-slate-400">· {action.owner_name}</span>}
              {action.project_name && <span className="text-slate-400">· {action.project_name}</span>}
            </li>
          ))}
        </ul>
      )}
      {!point.summary.length && !point.notes && !point.actions.length && (
        <p className="text-sm italic text-slate-400">Rien de noté sur ce point.</p>
      )}
    </article>
  );
};

const ENGAGEMENT_STATUS = {
  open: { label: 'À valider', tone: 'bg-amber-50 text-amber-700' },
  accepted: { label: 'Devenu une tâche', tone: 'bg-emerald-50 text-emerald-700' },
  dismissed: { label: 'Écarté', tone: 'bg-slate-100 text-slate-400' },
};

// Une réunion tactique close, relue après coup : tour des projets, ordre du jour, engagements relevés par Nexus.
const TacticalMeetingRecap = () => {
  const { meetingId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => apiService.get(`/circle-meetings/${meetingId}/recap`)
    .then((next) => { setData(next); setError(null); })
    .catch((err) => setError(err.message)), [meetingId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.nexus_review !== 'pending') return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [data?.nexus_review, load]);

  if (error) return <div className="p-6 text-sm text-rose-600">{error}</div>;
  if (!data) return <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</div>;

  const { meeting, participants, points, engagements } = data;
  const focus = data.focus || [];
  const withNews = data.projects.filter((p) => p.news.length || p.notes);
  const silent = data.projects.filter((p) => !p.news.length && !p.notes);
  const actionsCount = points.reduce((total, point) => total + point.actions.length, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl space-y-5 px-4 py-5">
        <Link to="/meetings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600">
          <ArrowLeft className="h-4 w-4" /> Mes réunions
        </Link>

        <header className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
                <RefreshCcw className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-indigo-600">
                  Réunion tactique{meeting.circle_name ? ` · ${meeting.circle_name}` : ''}
                </p>
                <h1 className="text-xl font-semibold text-slate-900">
                  {formatMeetingDate(meeting.started_at)}
                  {meeting.duration_seconds ? <span className="font-normal text-slate-400"> · {formatDuration(meeting.duration_seconds)}</span> : null}
                </h1>
              </div>
            </div>
            <NexusReviewBadge state={data.nexus_review} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {participants.map((person) => (
              <span key={person.id} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600">{person.name}</span>
            ))}
            {!data.attended && (
              <span className="flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs text-violet-700">
                <UserRound className="h-3 w-3" /> Tu n'y étais pas
              </span>
            )}
          </div>
          <p className="mt-3 text-sm text-slate-500">
            {withNews.length} projet{withNews.length > 1 ? 's' : ''} avec un quoi de neuf · {points.length} point{points.length > 1 ? 's' : ''} à l'ordre du jour
            {actionsCount ? ` · ${actionsCount} tâche${actionsCount > 1 ? 's' : ''} ou objectif${actionsCount > 1 ? 's' : ''} créé${actionsCount > 1 ? 's' : ''}` : ''}
          </p>
        </header>

        <Section icon={Target} title="Tour des projets" count={withNews.length}
          hint={data.nexus_review ? 'Écrit par Nexus pendant la réunion, relu après coup ; la version du secrétaire fait foi.' : null}>
          {withNews.length ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {withNews.map((project) => <ProjectCard key={project.project_id} project={project} />)}
            </div>
          ) : (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-5 text-center text-sm text-slate-400">Aucun quoi de neuf noté.</p>
          )}
          {silent.length > 0 && (
            <p className="px-1 text-xs text-slate-400">
              Aussi passés, sans rien de noté : {silent.map((p) => p.project_name).join(', ')}.
            </p>
          )}
        </Section>

        {focus.length > 0 && (
          <Section icon={Presentation} title="Focus tech" count={focus.length}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {focus.map((item) => (
                <Link key={item.id} to={`/tools/focus-tech/${item.id}`}
                      className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition hover:border-violet-200">
                  <div className="font-semibold text-slate-900">{item.title}</div>
                  {item.summary && <p className="mt-1 text-sm text-slate-500">{item.summary}</p>}
                  {item.notes?.length > 0 && <div className="mt-2"><Bullets items={item.notes} /></div>}
                  <p className="mt-2 text-xs text-slate-400">
                    Par {item.author_name} · {item.slide_count} diapo{item.slide_count > 1 ? 's' : ''} · revoir la présentation
                  </p>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {points.length > 0 && (
          <Section icon={ClipboardList} title="Ordre du jour" count={points.length}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {points.map((point) => <PointCard key={point.id} point={point} />)}
            </div>
          </Section>
        )}

        {engagements.length > 0 && (
          <Section icon={Sparkles} title="Engagements relevés par Nexus" count={engagements.length}
            hint="Relevés en relisant la réunion ; chacun les retrouve sur sa page d'accueil.">
            <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-100 bg-white shadow-sm">
              {engagements.map((item) => {
                const status = ENGAGEMENT_STATUS[item.status] || ENGAGEMENT_STATUS.open;
                return (
                  <li key={`${item.employee_name}-${item.title}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
                    <span className="w-28 shrink-0 truncate text-sm font-medium text-slate-800">{item.employee_name}</span>
                    <span className="min-w-0 flex-1 text-sm text-slate-700">
                      {item.title}
                      {item.project_name && <span className="text-slate-400"> · {item.project_name}</span>}
                      {item.due_date && <span className="text-slate-400"> · pour le {new Date(item.due_date).toLocaleDateString('fr-FR')}</span>}
                    </span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${status.tone}`}>{status.label}</span>
                  </li>
                );
              })}
            </ul>
          </Section>
        )}

        {data.notes && (
          <Section icon={MessageSquareText} title="Notes de réunion">
            <p className="whitespace-pre-wrap rounded-2xl border border-slate-100 bg-white p-4 text-sm text-slate-700 shadow-sm">{data.notes}</p>
          </Section>
        )}
      </div>
    </div>
  );
};

export default TacticalMeetingRecap;
